import { useAtomValue } from "jotai";
import { useMemo } from "react";
import {
  irrigationPlanHistoryLoadingAtom,
  irrigationPlanHistorySelectionReadyAtom,
  irrigationPlanHistorySummaryAtom,
} from "@/store/data";
import { dayjs } from "@/utils/date";

const numberFormatter = new Intl.NumberFormat("pt-BR");

function SummaryValue({ label, value, description }: { label: string; value: string; description?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
    </div>
  );
}

export function HistorySummaryCards() {
  const summary = useAtomValue(irrigationPlanHistorySummaryAtom);
  const loading = useAtomValue(irrigationPlanHistoryLoadingAtom);
  const selectionReady = useAtomValue(irrigationPlanHistorySelectionReadyAtom);

  const lastEventDescription = useMemo(() => {
    if (!summary.lastEntry) return "Sem registros recentes";
    const startLabel = dayjs(summary.lastEntry.startTime).calendar();
    if (summary.lastEntry.isOngoing) {
      return `Iniciado ${startLabel}`;
    }
    const endLabel = summary.lastEntry.endTime ? dayjs(summary.lastEntry.endTime).calendar() : null;
    return endLabel ? `Último encerrado ${endLabel}` : `Iniciado ${startLabel}`;
  }, [summary.lastEntry]);

  if (!selectionReady) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center text-sm text-neutral-600">
        Selecione um projeto ou plano para visualizar o resumo do histórico.
      </div>
    );
  }

  if (loading && summary.totalEntries === 0) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
            <div className="mt-3 h-8 w-24 animate-pulse rounded bg-gray-200" />
            <div className="mt-3 h-3 w-32 animate-pulse rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryValue
        label="Eventos no período"
        value={numberFormatter.format(summary.totalEntries)}
        description={lastEventDescription}
      />
      <SummaryValue
        label="Eventos em andamento"
        value={numberFormatter.format(summary.ongoingEntries)}
        description={summary.ongoingEntries > 0 ? "Planos em execução atualmente" : "Nenhum plano em execução"}
      />
      <SummaryValue
        label="Uso da bomba"
        value={numberFormatter.format(summary.usesWaterPumpCount)}
        description={
          summary.usesWaterPumpCount > 0
            ? `${summary.usesWaterPumpCount} registro(s) com bomba em uso`
            : "Nenhum evento utilizou bomba"
        }
      />
      <SummaryValue
        label="Fertirrigação"
        value={numberFormatter.format(summary.usesFertiCount)}
        description={
          summary.usesFertiCount > 0
            ? `${summary.usesFertiCount} ocorrência(s) com fertirrigação`
            : "Nenhum evento com fertirrigação"
        }
      />
    </div>
  );
}

export default HistorySummaryCards;
